'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { ENGINE_PATH_PREFIX } from '@gnsx/genesys.js';
import CRC32 from 'crc-32';
import { action, makeAutoObservable, reaction, runInAction } from 'mobx';

import { AssetType } from '../assetTypes';
import { isExcludedAssetPath } from '../excludedAssetPaths';

import { ThumbnailRenderer, type ThumbnailRendererType } from './thumbnailRenderer';

import type { IAssetFile } from '@/editor/store/models';
import type { IStorageProvider } from '@/lib/storage';

import { resolvePreviewToneMapping } from '@/editor/core/preview/resolvePreviewToneMapping';
import { editorStore, sceneStore } from '@/editor/store/';

const DEBUG_LOG = {
  initialization: true,
  generation: false,
  needsGenerateThumbnail: false,
  manifest: true,
  cleanup: true,
  directory: true,
  directoryChange: false,
  pause: true
} as const;

/**
 * When `false` (default), `@engine/...` assets never write thumbnails under
 * `@project/.editor/thumbnails/engine/...` — only pre-built URLs from `/engine-thumbnails/` are used.
 * Set to `true` locally to exercise per-project engine thumbnail generation (e.g. renderer debugging).
 */
export const ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION = false;

/** Thumbnail generation status */
export const ThumbnailStatus = {
  Idle: 'idle',
  Generating: 'generating',
  Completed: 'completed',
  Error: 'error'
} as const;
export type ThumbnailStatus = (typeof ThumbnailStatus)[keyof typeof ThumbnailStatus];

/** Thumbnail queue item */
interface ThumbnailQueueItem {
  asset: IAssetFile;
  priority: number;
  retryCount: number;
  verified?: boolean | 'skipped'; // Track if asset has been verified or verification was skipped
}

/** Thumbnail generation statistics */
interface ThumbnailStats {
  total: number;
  completed: number;
  failed: number;
  remaining: number;
}

/** Thumbnail manifest entry */
interface ThumbnailManifestEntry {
  checksum: string;
  generatedAt: string;
  thumbnailPath: string; // Empty string indicates failure
}

/** Thumbnail manifest - maps asset paths to their metadata */
interface ThumbnailManifest {
  [assetPath: string]: ThumbnailManifestEntry;
}

/** Static engine thumbnail manifest entry */
interface EngineThumbnailManifestEntry {
  /** Public static URL for the pre-built WebP, e.g. `/engine-thumbnails/assets/models/SM_Cube.glb.webp` */
  thumbnailPath: string;
}

/** Static engine thumbnail manifest - shipped in `public/engine-thumbnails/manifest.json` */
interface EngineThumbnailManifest {
  /** Engine package version that generated these thumbnails. */
  version: string;
  generatedAt: string;
  assets: Record<string, EngineThumbnailManifestEntry>;
  /** Material template previews: node-material asset class names and legacy `THREE.*Material` constructor names. */
  templates?: Record<string, EngineThumbnailManifestEntry>;
}

/**
 * Background thumbnail generation service
 * Manages queue, status, and coordination of thumbnail generation
 */
export class ThumbnailService {

  /** Current generation status */
  status: ThumbnailStatus = ThumbnailStatus.Idle;

  /** Queue items Map for O(1) lookups by asset path */
  private queueMap = new Map<string, ThumbnailQueueItem>();

  /** Priority-sorted queue array for processing order */
  private queue: ThumbnailQueueItem[] = [];

  /** Currently processing asset */
  currentAsset: IAssetFile | null = null;

  /** Generation statistics */
  stats: ThumbnailStats = {
    total: 0,
    completed: 0,
    failed: 0,
    remaining: 0
  };

  /** Max retry attempts */
  private readonly MAX_RETRIES = 1;

  /** Processing delay between batches (ms) */
  private readonly BATCH_DELAY = 5;

  /** WebP quality (0-100, default 85%) */
  private readonly WEBP_QUALITY = 85;

  /** Cooldown for failed thumbnails (5 minutes) */
  private readonly FAILED_RETRY_COOLDOWN = 5 * 60 * 1000; // 5 minutes in milliseconds

  /** Cleanup threshold for orphaned thumbnails (24 hours) */
  private readonly CLEANUP_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  /** Throttling for manifest saves */
  private readonly MANIFEST_SAVE_THROTTLE = 2000; // 2 seconds between manifest saves
  private readonly MANIFEST_MAX_FLUSH_PASSES = 3;
  private manifestSaveTimeout: NodeJS.Timeout | null = null;
  private pendingManifestUpdates = new Map<string, { asset: IAssetFile; success: boolean }>();
  private manifestFlushInFlight: Promise<void> | null = null;

  /** Debouncing for directory changes */
  private readonly DIRECTORY_CHANGE_DEBOUNCE = 300; // 300ms debounce for directory changes
  private directoryChangeTimeout: NodeJS.Timeout | null = null;

  /** Async lock for processing to prevent race conditions */
  private processingLock = false;

  /** Single thumbnail renderer instance */
  private renderer: ThumbnailRenderer | null = null;
  private desiredRendererType: ThumbnailRendererType = 'webgpu';

  /** Supported asset types for thumbnails */
  private readonly SUPPORTED_TYPES: Set<AssetType> = new Set([
    AssetType.Model,
    AssetType.Prefab,
    AssetType.Material
  ]);

  /** Processing state */
  private isProcessing = false;
  private processingPromise: Promise<void> | null = null;
  private isShuttingDown = false;
  private isDisposed = false;

  /** Pause state - pauses thumbnail generation during play mode or building */
  private isPaused = false;

  /** In-memory cache for thumbnail existence to avoid slow storage calls */
  private thumbnailExistsCache = new Map<string, boolean>();

  /** In-memory manifest cache to avoid repeated storage reads */
  private manifestCache: ThumbnailManifest | null = null;
  private manifestCacheLoaded = false;

  /** Static engine thumbnail manifest - loaded once from `/engine-thumbnails/manifest.json` */
  private engineManifest: EngineThumbnailManifest | null = null;
  private engineManifestLoading: Promise<void> | null = null;

  /** Event listeners for thumbnail generation completion */
  private thumbnailGeneratedListeners = new Set<(asset: IAssetFile) => void>();

  /** Dedupe repeated console output for identical thumbnail failures / resets. */
  private loggedThumbnailFailureKeys = new Set<string>();
  private loggedThumbnailResetKeys = new Set<string>();
  private loggedManifestDropKeys = new Set<string>();

  /** Maximum listeners to prevent memory leaks */
  private readonly MAX_LISTENERS = 100;

  /** MobX reaction disposers for cleanup */
  private disposers: Array<() => void> = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    // Watch for play mode changes
    this.disposers.push(
      reaction(
        () => editorStore.scene?.isPlayMode,
        (isPlayMode) => {
          this.checkAndResume();
        }
      )
    );

    // Watch for building state changes
    this.disposers.push(
      reaction(
        () => editorStore.isBuildingProject,
        (isBuildingProject) => {
          this.checkAndResume();
        }
      )
    );

    // Keep thumbnail renderer backend in sync with viewport backend.
    // Default to WebGPU when scene state is unavailable.
    this.disposers.push(
      reaction(
        () => (editorStore.scene?.isWebGPU === false ? 'webgl' : 'webgpu') as ThumbnailRendererType,
        rendererType => {
          this.desiredRendererType = rendererType;
        },
        { fireImmediately: true }
      )
    );

    if (DEBUG_LOG.initialization) console.log('[thumbnailService] Thumbnail service initialized (single renderer, sequential processing).');

    // Pre-fetch the static engine thumbnail manifest in the background.
    void this.getEngineManifest();
  }

  /** Return true when the asset is an `@engine/...` path. */
  private isEngineAsset(asset: IAssetFile): boolean {
    return asset.path.startsWith(ENGINE_PATH_PREFIX);
  }

  /**
   * Lazy-load the static engine thumbnail manifest from `/engine-thumbnails/manifest.json`.
   * Resolves immediately after the first successful fetch; subsequent calls return the cached promise.
   */
  private getEngineManifest(): Promise<void> {
    if (this.engineManifest !== null) return Promise.resolve();
    if (this.engineManifestLoading) return this.engineManifestLoading;

    this.engineManifestLoading = fetch('/engine-thumbnails/manifest.json')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<EngineThumbnailManifest>;
      })
      .then(manifest => {
        this.engineManifest = manifest;
        if (DEBUG_LOG.initialization) {
          const count = Object.keys(manifest.assets ?? {}).length;
          console.log(`[thumbnailService] Loaded engine thumbnail manifest v${manifest.version} (${count} assets).`);
        }
      })
      .catch(err => {
        // Non-fatal: without a manifest, engine assets use static URLs only (no project writes when
        // ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION is false).
        console.warn('[thumbnailService] Failed to load engine thumbnail manifest:', err);
        this.engineManifest = { version: '', generatedAt: '', assets: {}, templates: {} };
      })
      .finally(() => {
        this.engineManifestLoading = null;
      });

    return this.engineManifestLoading;
  }

  /** Return the static public URL for a pre-built engine thumbnail, or null if not in manifest. */
  private getEngineThumbnailUrl(asset: IAssetFile): string | null {
    const entry = this.engineManifest?.assets[asset.path];
    return entry?.thumbnailPath ?? null;
  }

  /**
   * Return the static public URL for a material template thumbnail (`/engine-thumbnails/templates/material/...`),
   * or null if not in manifest.
   *
   * Keys are constructor names: engine node-material asset classes (e.g. `MeshStandardNodeMaterialAsset`)
   * and legacy built-in THREE materials (e.g. `MeshStandardMaterial`).
   */
  async getMaterialTemplateThumbnailUrl(templateKey: string): Promise<string | null> {
    await this.getEngineManifest();
    const entry = this.engineManifest?.templates?.[templateKey];
    return entry?.thumbnailPath ?? null;
  }

  /**
   * @deprecated Prefer `getMaterialTemplateThumbnailUrl` — kept for call sites that still use the old name.
   */
  async getNodeMaterialTemplateThumbnailUrl(className: string): Promise<string | null> {
    return this.getMaterialTemplateThumbnailUrl(className);
  }

  /** Check if asset type supports thumbnails */
  isSupported(assetType: AssetType): boolean {
    return this.SUPPORTED_TYPES.has(assetType);
  }

  private isExcludedAsset(asset: IAssetFile): boolean {
    return isExcludedAssetPath(asset.path);
  }

  /** Check if thumbnail generation should be paused */
  private shouldBePaused(): boolean {
    // Pause while service is shutting down/disposed to avoid further I/O.
    if (this.isShuttingDown || this.isDisposed) {
      return true;
    }

    // Pause during play mode
    if (editorStore.scene?.isPlayMode) {
      return true;
    }

    // Pause during project build
    if (editorStore.isBuildingProject) {
      return true;
    }

    return false;
  }

  /** Update pause state and log changes */
  @action
  private updatePauseState(): void {
    const shouldPause = this.shouldBePaused();

    if (shouldPause !== this.isPaused) {
      this.isPaused = shouldPause;

      if (DEBUG_LOG.pause) {
        if (this.isPaused) {
          let reason = 'project building';
          if (this.isShuttingDown || this.isDisposed) {
            reason = 'service shutdown';
          } else if (editorStore.scene?.isPlayMode) {
            reason = 'play mode active';
          }
          console.log(`[thumbnailService] Paused thumbnail generation (${reason}).`);
        } else {
          console.log('[thumbnailService] Resumed thumbnail generation.');
        }
      }
    }
  }

  /** Check pause state and resume processing if needed */
  checkAndResume(): void {
    this.updatePauseState();

    // If no longer paused and there's work to do, resume processing
    if (!this.isPaused && this.queue.length > 0 && !this.isProcessing) {
      this.startProcessing();
    }
  }

  /** Queue asset for thumbnail generation */
  @action
  queueThumbnailGeneration(asset: IAssetFile, priority: number = 0, forceRefresh: boolean = false): void {
    if (this.isShuttingDown || this.isDisposed) {
      return;
    }

    if (!this.isSupported(asset.assetType) || this.isExcludedAsset(asset)) {
      return;
    }

    // Engine assets: use static `/engine-thumbnails/...` only unless explicitly testing project writes.
    if (this.isEngineAsset(asset)) {
      const staticUrl = this.getEngineThumbnailUrl(asset);
      if (staticUrl !== null) {
        return;
      }
      if (!ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION) {
        return;
      }
    }

    // Check if already queued using Map (O(1) lookup)
    const existing = this.queueMap.get(asset.path);
    if (existing) {
      if (forceRefresh) {
        existing.verified = 'skipped';
      }
      // Update priority if higher
      if (priority > existing.priority) {
        existing.priority = priority;
        this.sortQueue();
      }
      return;
    }

    // Create new queue item
    const item: ThumbnailQueueItem = {
      asset,
      priority,
      retryCount: 0,
      verified: forceRefresh ? 'skipped' : false
    };

    // Add to both Map and array
    this.queueMap.set(asset.path, item);
    this.queue.push(item);
    this.sortQueue();

    this.stats.total++; // Increment total when adding new item
    this.updateStats();
    this.startProcessing();
  }

  /** Remove asset from queue */
  @action
  cancelThumbnailGeneration(assetPath: string): void {
    // Check if in queue using Map (O(1))
    if (!this.queueMap.has(assetPath)) {
      return;
    }

    // Remove from Map
    this.queueMap.delete(assetPath);

    // Remove from array
    const queueIndex = this.queue.findIndex(item => item.asset.path === assetPath);
    if (queueIndex !== -1) {
      this.queue.splice(queueIndex, 1);
      this.stats.total--; // Decrement total when removing item
    }

    this.updateStats();
  }

  /** Clear all queued thumbnails */
  @action
  clearQueue(): void {
    const itemsRemoved = this.queue.length;
    this.queueMap.clear();
    this.queue.length = 0;
    this.stats.total = Math.max(0, this.stats.total - itemsRemoved);
    this.updateStats();
  }

  /** Force flush any pending manifest updates */
  async forceFlushManifest(): Promise<void> {
    if (this.manifestSaveTimeout) {
      clearTimeout(this.manifestSaveTimeout);
      this.manifestSaveTimeout = null;
    }
    await this.flushManifestUpdatesSerialized();
  }

  /** Prepare for shutdown by stopping new queue work and pausing processing. */
  prepareShutdown(): void {
    if (this.isDisposed || this.isShuttingDown) {
      return;
    }
    this.isShuttingDown = true;
    this.clearQueue();
    this.updatePauseState();
  }

  /** Wait for in-flight queue processing to settle before disposal. */
  async waitForIdle(timeoutMs: number = 7000): Promise<boolean> {
    const activeProcessing = this.processingPromise;
    if (!activeProcessing) {
      return true;
    }

    let timeoutHandle: NodeJS.Timeout | null = null;
    try {
      return await Promise.race([
        activeProcessing.then(() => true).catch(() => true),
        new Promise<boolean>(resolve => {
          timeoutHandle = setTimeout(() => resolve(false), timeoutMs);
        }),
      ]);
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    }
  }

  /**
   * Removes `@project/.editor/thumbnails` from disk (all `.webp` previews and `.manifest.json`),
   * clears in-memory caches and the generation queue, and bumps loaded assets so the UI drops stale images.
   * Does not delete other `.editor` data (e.g. `build-stamp.json`).
   */
  async clearAllStoredThumbnails(): Promise<void> {
    if (this.manifestSaveTimeout) {
      clearTimeout(this.manifestSaveTimeout);
      this.manifestSaveTimeout = null;
    }
    this.pendingManifestUpdates.clear();

    this.clearQueue();

    const storageProvider = editorStore.project.makeScopedStorageProvider() as IStorageProvider;
    const thumbnailsRoot = ENGINE.AssetPath.fromString('@project/.editor/thumbnails');

    try {
      const exists = await storageProvider.exists(thumbnailsRoot, { silent: true });
      if (exists) {
        await storageProvider.deleteItem(thumbnailsRoot, true);
      }
    } catch (error) {
      console.error('[thumbnailService] Failed to delete thumbnails directory:', error);
      throw error;
    }

    runInAction(() => {
      this.stats = { total: 0, completed: 0, failed: 0, remaining: 0 };
      this.status = ThumbnailStatus.Idle;
      this.currentAsset = null;
      this.thumbnailExistsCache.clear();
      this.manifestCache = {};
      this.manifestCacheLoaded = true;
      this.isProcessing = false;
      this.processingPromise = null;
      this.processingLock = false;
    });

    const files = editorStore.assets?.assetTree.allFiles ?? [];
    for (const asset of files) {
      if (asset.supportsThumbnails) {
        asset.refreshThumbnail();
      }
    }

    if (DEBUG_LOG.cleanup) {
      console.log('[thumbnailService] Cleared all stored thumbnails under .editor/thumbnails');
    }
  }

  /** Get or create single renderer instance */
  private getRenderer(): ThumbnailRenderer {
    if (!this.renderer || this.renderer.getRequestedRendererType() !== this.desiredRendererType) {
      if (this.renderer) {
        this.renderer.dispose();
      }
      this.renderer = new ThumbnailRenderer(this.desiredRendererType);
    }
    return this.renderer;
  }

  /** Dispose stale renderer instance after backend/pipeline failures. */
  private resetRenderer(reason: string, error?: unknown): void {
    if (!this.renderer) {
      return;
    }
    const message = error instanceof Error ? error.message : String(error ?? '');
    const dedupeKey = `${reason}|${message.slice(0, 160)}`;
    if (!this.loggedThumbnailResetKeys.has(dedupeKey)) {
      this.loggedThumbnailResetKeys.add(dedupeKey);
      console.warn(`[thumbnailService] Resetting thumbnail renderer (${reason}).`, error);
    }
    this.renderer.dispose();
    this.renderer = null;
  }

  /**
   * Only reset the WebGPU thumbnail renderer when the error plausibly indicates a bad
   * device/context — not invalid vertex layout (content bugs), which resetting does not fix.
   */
  private shouldResetRendererAfterError(error: unknown): boolean {
    if (this.desiredRendererType !== 'webgpu') {
      return false;
    }
    const message = error instanceof Error ? error.message : String(error ?? '');
    if (
      message.includes('GPUVertexFormat') ||
      message.includes('GPUVertexAttribute') ||
      /unorm32|snorm32/i.test(message)
    ) {
      return false;
    }
    const lower = message.toLowerCase();
    return (
      lower.includes('device lost') ||
      lower.includes('devicelost') ||
      (message.includes('GPUDevice') && lower.includes('destroy'))
    );
  }

  /** Reset session state for new project */
  resetSession(): void {
    this.clearQueue();
    this.stats = { total: 0, completed: 0, failed: 0, remaining: 0 };
    this.status = ThumbnailStatus.Idle;
    this.currentAsset = null;
    this.thumbnailExistsCache.clear(); // Clear cache for new project
    this.manifestCache = null; // Clear manifest cache for new project
    this.manifestCacheLoaded = false;
    this.thumbnailGeneratedListeners.clear(); // Clear listeners for new project

    // Clear any pending timeouts
    if (this.directoryChangeTimeout) {
      clearTimeout(this.directoryChangeTimeout);
      this.directoryChangeTimeout = null;
    }
    if (this.manifestSaveTimeout) {
      clearTimeout(this.manifestSaveTimeout);
      this.manifestSaveTimeout = null;
    }
    this.pendingManifestUpdates.clear();

    this.loggedThumbnailFailureKeys.clear();
    this.loggedThumbnailResetKeys.clear();
    this.loggedManifestDropKeys.clear();

    // Reset processing state
    this.isProcessing = false;
    this.processingPromise = null;
    this.processingLock = false;
    this.isPaused = false;
    this.isShuttingDown = false;
    this.isDisposed = false;

    // Dispose renderer to free WebGL resources
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }
  }

  /** Dispose of renderer resources */
  dispose(): void {
    this.isShuttingDown = true;
    this.isDisposed = true;
    this.clearQueue();

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }

    if (this.manifestSaveTimeout) {
      clearTimeout(this.manifestSaveTimeout);
      this.manifestSaveTimeout = null;
    }
    this.pendingManifestUpdates.clear();

    if (this.directoryChangeTimeout) {
      clearTimeout(this.directoryChangeTimeout);
      this.directoryChangeTimeout = null;
    }

    // Dispose MobX reactions
    this.disposers.forEach(dispose => dispose());
    this.disposers = [];

    // Clear all listeners on disposal
    this.thumbnailGeneratedListeners.clear();
  }

  /** Get thumbnail path for asset - preserves namespace and relative structure */
  getThumbnailPath(asset: IAssetFile): string {
    // Engine assets: use the pre-built static public URL when available.
    if (this.isEngineAsset(asset)) {
      const staticUrl = this.getEngineThumbnailUrl(asset);
      if (staticUrl) return staticUrl;

      // Manifest not loaded yet or asset not listed — fall back to the conventional
      // per-project path when ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION is enabled.
      const pathWithoutPrefix = this.getThumbnailRelativePath(asset.path);
      return `@project/.editor/thumbnails/${pathWithoutPrefix}.webp`;
    }

    // Preserve root namespace to avoid collisions (e.g., @engine vs @project)
    const pathWithoutPrefix = this.getThumbnailRelativePath(asset.path);

    // Build thumbnail path maintaining directory structure
    // e.g., @project/models/character.gltf -> @project/.editor/thumbnails/models/character.gltf.webp
    return `@project/.editor/thumbnails/${pathWithoutPrefix}.webp`;
  }

  /** Normalize an asset path to a namespaced thumbnail-relative path. */
  private getThumbnailRelativePath(assetPath: string): string {
    const namespacedPath = assetPath.match(/^@([^/]+)\/(.+)$/);
    if (namespacedPath) {
      return `${namespacedPath[1]}/${namespacedPath[2]}`;
    }
    return assetPath.replace(/^@/, '');
  }

  /**
   * Check if thumbnail exists with permanent caching (invalidated only on file changes).
   * Use `bypassCache` to force a storage read (e.g. manifest vs disk mismatch).
   */
  async thumbnailExists(
    asset: IAssetFile,
    options?: { bypassCache?: boolean }
  ): Promise<boolean> {
    if (this.isExcludedAsset(asset)) {
      return false;
    }

    // Engine assets listed in the static manifest always have a thumbnail.
    if (this.isEngineAsset(asset)) {
      await this.getEngineManifest();
      if (this.getEngineThumbnailUrl(asset) !== null) return true;
      if (!ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION) {
        return false;
      }
      // Not in manifest — fall through to per-project storage check when generation is allowed.
    }

    const thumbnailPath = this.getThumbnailPath(asset);

    // Check cache first - never expires, only invalidated manually
    if (!options?.bypassCache) {
      const cached = this.thumbnailExistsCache.get(thumbnailPath);
      if (cached !== undefined) {
        return cached;
      }
    }

    // Check storage and cache result permanently
    try {
      const storageProvider = editorStore.project.makeScopedStorageProvider() as IStorageProvider;
      // Suppress errors for thumbnail existence checks since they're expected to fail often
      const exists = await storageProvider.exists(
        ENGINE.AssetPath.fromString(thumbnailPath),
        { silent: true }
      );

      // Cache the result permanently
      this.thumbnailExistsCache.set(thumbnailPath, exists);

      return exists;
    } catch (error) {
      // Cache negative result on error
      this.thumbnailExistsCache.set(thumbnailPath, false);
      return false;
    }
  }

  /** Mark thumbnail as existing in cache (called after successful generation) */
  private markThumbnailAsGenerated(asset: IAssetFile): void {
    const thumbnailPath = this.getThumbnailPath(asset);
    this.thumbnailExistsCache.set(thumbnailPath, true);
  }

  /** Mark thumbnail as non-existing in cache (called after deletion) */
  private markThumbnailAsDeleted(asset: IAssetFile): void {
    const thumbnailPath = this.getThumbnailPath(asset);
    this.thumbnailExistsCache.set(thumbnailPath, false);
  }

  /** Invalidate cache entries for changed/deleted assets */
  invalidateThumbnailCache(assetPaths: string[]): void {
    for (const assetPath of assetPaths) {
      // Use consistent path calculation matching getThumbnailPath
      const pathWithoutPrefix = this.getThumbnailRelativePath(assetPath);
      const thumbnailPath = `@project/.editor/thumbnails/${pathWithoutPrefix}.webp`;

      // Remove from cache to force re-check on next access
      this.thumbnailExistsCache.delete(thumbnailPath);
    }

    if (assetPaths.length > 0 && DEBUG_LOG.cleanup) {
      console.debug(`[thumbnailService] Invalidated thumbnail cache for ${assetPaths.length} changed assets.`);
    }
  }

  /** Add listener for thumbnail generation events */
  onThumbnailGenerated(listener: (asset: IAssetFile) => void): () => void {
    // Check for memory leak - warn if too many listeners
    if (this.thumbnailGeneratedListeners.size >= this.MAX_LISTENERS) {
      console.warn(`[thumbnailService] Too many thumbnail listeners (${this.thumbnailGeneratedListeners.size}). Possible memory leak - cleanup functions not being called.`);
    }

    this.thumbnailGeneratedListeners.add(listener);
    // Return cleanup function
    return () => this.thumbnailGeneratedListeners.delete(listener);
  }

  /** Emit thumbnail generated event */
  private emitThumbnailGenerated(asset: IAssetFile): void {
    this.thumbnailGeneratedListeners.forEach(listener => {
      try {
        listener(asset);
      } catch (error) {
        console.warn('[thumbnailService] Error in thumbnail generated listener:', error);
      }
    });
  }

  /** Calculate CRC32 checksum for asset (path + modified time + size + contentType) */
  private calculateAssetChecksum(asset: IAssetFile): string {
    // Normalize path to avoid case/separator issues across platforms
    const normalizedPath = asset.path.toLowerCase().replace(/\\/g, '/');

    // Round modifiedTime to seconds to avoid precision/timezone issues
    const modifiedTimeSeconds = Math.floor(asset.modifiedTime.getTime() / 1000);

    // Validate modifiedTime is not invalid
    if (isNaN(modifiedTimeSeconds) || modifiedTimeSeconds <= 0) {
      // Invalid date, use path + contentType (if available)
      if (DEBUG_LOG.needsGenerateThumbnail) {
        console.warn(`[thumbnailService] ${asset.path} has invalid modifiedTime, using path-only checksum.`);
      }
      const fallbackData = asset.contentType
        ? `${normalizedPath}|${asset.contentType}`
        : normalizedPath;
      return CRC32.str(fallbackData).toString(36);
    }

    // Build checksum data with all available reliable fields
    const checksumParts = [
      normalizedPath,
      modifiedTimeSeconds.toString(),
    ];

    // Include size if available and non-zero
    if (asset.size && asset.size > 0) {
      checksumParts.push(asset.size.toString());
    }

    // Include contentType for additional reliability (helps detect file type changes)
    if (asset.contentType) {
      checksumParts.push(asset.contentType);
    }

    const data = checksumParts.join('|');
    return CRC32.str(data).toString(36);
  }

  /** Check if thumbnail should be generated */
  async needsGenerateThumbnail(asset: IAssetFile): Promise<boolean> {
    // Engine assets in the static manifest are always up-to-date.
    if (this.isEngineAsset(asset)) {
      await this.getEngineManifest();
      if (this.getEngineThumbnailUrl(asset) !== null) return false;
      if (!ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION) return false;
    }

    try {
      // Check manifest first (fastest path)
      const manifest = await this.getThumbnailManifest();
      const manifestEntry = manifest[asset.path];

      // If no manifest entry exists, we need a thumbnail
      if (!manifestEntry) {
        if (DEBUG_LOG.needsGenerateThumbnail) console.log(`[thumbnailService] ${asset.path} needs thumbnail: No manifest entry.`);
        return true;
      }

      // Calculate current asset checksum
      const currentChecksum = this.calculateAssetChecksum(asset);

      // If checksum changed, always regenerate (no cooldown)
      if (manifestEntry.checksum !== currentChecksum) {
        if (DEBUG_LOG.needsGenerateThumbnail) {
          const modifiedTimeSeconds = Math.floor(asset.modifiedTime.getTime() / 1000);
          const modifiedTimeISO = asset.modifiedTime.toISOString();
          console.log(`[thumbnailService] ${asset.path} needs thumbnail: Checksum changed.`, {
            was: manifestEntry.checksum,
            now: currentChecksum,
            modifiedTime: modifiedTimeISO,
            modifiedTimeSeconds,
            size: asset.size,
            path: asset.path
          });
        }
        return true;
      }

      // If checksum is same but previous generation failed (empty path)
      if (!manifestEntry.thumbnailPath) {
        const lastAttempt = new Date(manifestEntry.generatedAt).getTime();
        const cooldownExpired = Date.now() - lastAttempt > this.FAILED_RETRY_COOLDOWN;
        if (DEBUG_LOG.needsGenerateThumbnail) {
          const minutesSinceAttempt = Math.round((Date.now() - lastAttempt) / 60000);
          console.log(`[thumbnailService] ${asset.path} needs thumbnail: Previous generation failed, cooldown ${cooldownExpired ? 'expired' : 'active'} (${minutesSinceAttempt}m ago).`);
        }
        return cooldownExpired;
      }

      // Checksum matches — still verify the file is on disk (manifest can be stale if
      // thumbnails were removed outside the editor).
      const existsOnDisk = await this.thumbnailExists(asset, { bypassCache: true });
      if (!existsOnDisk) {
        if (DEBUG_LOG.needsGenerateThumbnail) {
          console.log(`[thumbnailService] ${asset.path} needs thumbnail: manifest OK but file missing on disk.`);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.log(`[thumbnailService] ${asset.path} needs thumbnail: Error checking (${error}).`);
      return true;
    }
  }


  /** Get thumbnail manifest (persistent metadata) - cached in memory */
  private async getThumbnailManifest(): Promise<ThumbnailManifest> {
    // Return cached manifest if available
    if (this.manifestCacheLoaded && this.manifestCache !== null) {
      return this.manifestCache;
    }

    try {
      const storageProvider = editorStore.project.makeScopedStorageProvider();
      const manifestPath = ENGINE.AssetPath.fromString('@project/.editor/thumbnails/.manifest.json');

      const manifestExists = await storageProvider.exists(manifestPath, { silent: true });
      if (!manifestExists) {
        this.manifestCache = {};
        this.manifestCacheLoaded = true;
        return {};
      }

      const manifestContent = await storageProvider.downloadFileAsText(manifestPath);
      const parsedManifest = ENGINE.parseJson(manifestContent);
      this.manifestCache = parsedManifest;
      this.manifestCacheLoaded = true;
      return parsedManifest;
    } catch (error) {
      this.manifestCache = {};
      this.manifestCacheLoaded = true;
      return {};
    }
  }

  /** Invalidate manifest cache - call when manifest is modified */
  private invalidateManifestCache(): void {
    this.manifestCacheLoaded = false;
    this.manifestCache = null;
  }

  /** Update thumbnail manifest after generating a thumbnail (throttled) */
  private updateThumbnailManifest(asset: IAssetFile, success: boolean = true): void {
    // Store the update request
    this.pendingManifestUpdates.set(asset.path, { asset, success });

    // Clear existing timeout
    if (this.manifestSaveTimeout) {
      clearTimeout(this.manifestSaveTimeout);
    }

    // Schedule throttled save
    this.manifestSaveTimeout = setTimeout(() => {
      this.manifestSaveTimeout = null;
      void this.flushManifestUpdatesSerialized();
    }, this.MANIFEST_SAVE_THROTTLE);
  }

  /** Serialize manifest flushes to avoid concurrent read-modify-write cycles. */
  private async flushManifestUpdatesSerialized(): Promise<void> {
    if (this.manifestFlushInFlight) {
      await this.manifestFlushInFlight;
      return;
    }

    this.manifestFlushInFlight = (async () => {
      let pass = 0;
      while (this.pendingManifestUpdates.size > 0 && pass < this.MANIFEST_MAX_FLUSH_PASSES) {
        const pendingBeforePass = this.pendingManifestUpdates.size;
        await this.flushManifestUpdates();
        pass++;

        const pendingAfterPass = this.pendingManifestUpdates.size;
        if (pendingAfterPass === 0) {
          break;
        }
        if (pendingAfterPass >= pendingBeforePass) {
          break;
        }
      }

      if (this.pendingManifestUpdates.size > 0 && !this.isShuttingDown && !this.isDisposed) {
        console.warn(
          `[thumbnailService] Deferred ${this.pendingManifestUpdates.size} manifest updates after flush retries.`
        );
      }
    })();

    try {
      await this.manifestFlushInFlight;
    } finally {
      this.manifestFlushInFlight = null;
    }
  }

  /** Flush all pending manifest updates to storage */
  private async flushManifestUpdates(): Promise<void> {
    if (this.pendingManifestUpdates.size === 0) return;
    const updatesToFlush = Array.from(this.pendingManifestUpdates.entries());
    this.pendingManifestUpdates.clear();

    try {
      const manifest = await this.getThumbnailManifest();

      // Apply all pending updates
      for (const [assetPath, { asset, success }] of updatesToFlush) {
        const checksum = this.calculateAssetChecksum(asset);

        manifest[assetPath] = {
          checksum,
          generatedAt: new Date().toISOString(),
          thumbnailPath: success ? this.getThumbnailPath(asset) : '' // Empty path indicates failure
        };
      }

      // Update cache with modified manifest
      this.manifestCache = manifest;

      // Save to storage
      const storageProvider = editorStore.project.makeScopedStorageProvider();
      const manifestPath = ENGINE.AssetPath.fromString('@project/.editor/thumbnails/.manifest.json');

      await storageProvider.uploadFile(
        manifestPath,
        JSON.stringify(manifest, null, 2),
        { contentType: 'application/json' }
      );

      if (DEBUG_LOG.manifest) console.debug(`[thumbnailService] Updated thumbnail manifest with ${updatesToFlush.length} changes.`);
      this.manifestSaveTimeout = null;
    } catch (error) {
      console.error('[thumbnailService] Failed to update thumbnail manifest:', error);
      // Invalidate cache on error to force reload next time
      this.invalidateManifestCache();

      if (this.shouldRequeueManifestUpdates(error)) {
        // Requeue failed updates unless a newer update for the same path already exists.
        for (const [assetPath, update] of updatesToFlush) {
          if (!this.pendingManifestUpdates.has(assetPath)) {
            this.pendingManifestUpdates.set(assetPath, update);
          }
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : String(error ?? '');
        const dropKey = `${updatesToFlush.length}|${errorMessage.slice(0, 160)}`;
        if (!this.loggedManifestDropKeys.has(dropKey)) {
          this.loggedManifestDropKeys.add(dropKey);
          console.warn(
            `[thumbnailService] Dropping ${updatesToFlush.length} manifest updates after non-retryable failure.`,
            error
          );
        }
      }
      this.manifestSaveTimeout = null;
    }
  }

  private shouldRequeueManifestUpdates(error: unknown): boolean {
    if (this.isShuttingDown || this.isDisposed) {
      return false;
    }
    return !this.isTransportFailure(error);
  }

  private isTransportFailure(error: unknown): boolean {
    const message = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();
    return (
      message.includes('failed to fetch') ||
      message.includes('network error') ||
      message.includes('network request failed') ||
      message.includes('xhr') ||
      message.includes('econnrefused') ||
      message.includes('enotfound') ||
      message.includes('socket')
    );
  }


  /** Start processing queue with async lock to prevent race conditions */
  private async startProcessing(): Promise<void> {
    // Update pause state before processing
    this.updatePauseState();

    // Check if paused
    if (this.isPaused) return;

    // Check if already processing or queue is empty
    if (this.isProcessing || this.queue.length === 0) return;

    // Acquire lock (atomic check-and-set)
    if (this.processingLock) return;
    this.processingLock = true;

    try {
      this.setProcessingState(true, ThumbnailStatus.Generating);

      this.processingPromise = this.processQueue();
      await this.processingPromise;

      this.setProcessingState(false, this.queue.length > 0 ? ThumbnailStatus.Idle : ThumbnailStatus.Completed);
      this.setCurrentAsset(null);
    } finally {
      // Always release lock
      this.processingLock = false;
    }
  }

  /** Process queue sequentially */
  private async processQueue(): Promise<void> {
    while (this.queue.length > 0) {
      // Check if we should pause before processing each item
      this.updatePauseState();
      if (this.isPaused) {
        // Put the item back and stop processing
        return;
      }

      const item = this.shiftQueue()!; // Take one item at a time

      try {
        await this.processQueueItem(item);
      } catch (error) {
        // Individual item errors are already handled in processQueueItem
        console.warn(`[thumbnailService] Error processing thumbnail for ${item.asset.path}:`, error);
      }

      this.updateStats();

      // Small delay between thumbnails to avoid blocking
      if (this.queue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, this.BATCH_DELAY));
      }
    }
  }

  /** Process single queue item with inline verification */
  private async processQueueItem(item: ThumbnailQueueItem): Promise<void> {
    this.setCurrentAsset(item.asset);

    try {
      if (this.isEngineAsset(item.asset)) {
        await this.getEngineManifest();
        if (
          this.getEngineThumbnailUrl(item.asset) === null &&
          !ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION
        ) {
          if (DEBUG_LOG.generation) {
            console.log(`[thumbnailService] ${item.asset.path} — engine project thumbnails disabled, skipping queue item.`);
          }
          this.incrementCompleted();
          this.queueMap.delete(item.asset.path);
          return;
        }
      }

      // Check if thumbnail is actually needed (inline verification)
      // Skip check only if force refresh was used (verified === 'skipped')
      if (item.verified !== 'skipped') {
        const needsGeneration = await this.needsGenerateThumbnail(item.asset);
        if (!needsGeneration) {
          if (DEBUG_LOG.generation) console.log(`[thumbnailService] ${item.asset.path} already has valid thumbnail, skipping.`);
          this.incrementCompleted();
          // Remove from map since we're done with it
          this.queueMap.delete(item.asset.path);
          return;
        }
      } else {
        if (DEBUG_LOG.generation) console.log(`[thumbnailService] Force generating thumbnail for ${item.asset.path} (verification skipped).`);
      }

      // Get the single renderer instance
      const renderer = this.getRenderer();

      const toneMapping = resolvePreviewToneMapping(
        editorStore.inspectorPanel.previewToneMapping,
        sceneStore.world,
      );

      // Generate thumbnail with WebP quality
      const thumbnailBlob = await renderer.generateThumbnail(item.asset, this.WEBP_QUALITY, toneMapping);

      if (this.isShuttingDown || this.isDisposed) {
        this.queueMap.delete(item.asset.path);
        this.incrementCompleted();
        return;
      }

      // Save thumbnail
      const saved = await this.saveThumbnail(item.asset, thumbnailBlob);
      if (!saved) {
        this.queueMap.delete(item.asset.path);
        this.incrementCompleted();
        return;
      }

      // Mark as generated in cache
      this.markThumbnailAsGenerated(item.asset);

      // Update manifest with successful generation
      this.updateThumbnailManifest(item.asset, true);

      this.incrementCompleted();

      // Remove from map since generation succeeded
      this.queueMap.delete(item.asset.path);

      // Emit thumbnail completion event for UI refresh
      this.emitThumbnailGenerated(item.asset);

      item.asset.refreshThumbnail();

    } catch (error) {
      if (this.shouldResetRendererAfterError(error)) {
        this.resetRenderer('WebGPU pipeline failure during thumbnail generation', error);
      }

      // Retry logic
      if (item.retryCount < this.MAX_RETRIES) {
        item.retryCount++;
        this.queue.unshift(item); // Add back to front of queue (keep in map)
      } else {
        // Mark as failed in manifest after max retries
        this.updateThumbnailManifest(item.asset, false);
        this.incrementFailed();
        // Remove from map since we're done with retries
        this.queueMap.delete(item.asset.path);
        const errMsg = error instanceof Error ? error.message : String(error ?? '');
        const failKey = `${item.asset.path}|${errMsg.slice(0, 160)}`;
        if (!this.loggedThumbnailFailureKeys.has(failKey)) {
          this.loggedThumbnailFailureKeys.add(failKey);
          console.warn(
            `[thumbnailService] Failed to generate thumbnail after ${this.MAX_RETRIES} retries: ${item.asset.path}`,
            error
          );
        }
      }
    }
  }

  /** Save thumbnail to storage */
  /** Save thumbnail to storage. Returns `false` when the write is intentionally skipped (no-op). */
  private async saveThumbnail(asset: IAssetFile, thumbnailBlob: Blob): Promise<boolean> {
    if (this.isShuttingDown || this.isDisposed) {
      return false;
    }

    const thumbnailPath = this.getThumbnailPath(asset);
    if (
      this.isEngineAsset(asset) &&
      !ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION &&
      !thumbnailPath.startsWith('/engine-thumbnails')
    ) {
      console.warn(
        `[thumbnailService] Refusing to save engine thumbnail under project path (set ALLOW_ENGINE_THUMBNAIL_PROJECT_GENERATION to test): ${asset.path} → ${thumbnailPath}`,
      );
      return false;
    }
    const storageProvider = editorStore.project.makeScopedStorageProvider();

    // Ensure parent directory exists (creates all nested directories)
    // e.g., for thumbnails/models/characters/hero.gltf.webp, creates thumbnails/models/characters/
    const lastSlashIndex = thumbnailPath.lastIndexOf('/');
    if (lastSlashIndex > 0) {
      const parentDir = thumbnailPath.substring(0, lastSlashIndex);
      await storageProvider.createDirectory(ENGINE.AssetPath.fromString(parentDir));
    }

    // Save thumbnail
    try {
      await storageProvider.uploadFile(
        ENGINE.AssetPath.fromString(thumbnailPath),
        thumbnailBlob,
        { contentType: 'image/webp' }
      );
    } catch (error) {
      console.warn(`[thumbnailService] Failed to save thumbnail: ${thumbnailPath}`, error);
      throw error;
    }
    return true;
  }

  /** Sort queue by priority (higher first) */
  @action
  private sortQueue(): void {
    this.queue.sort((a, b) => b.priority - a.priority);
  }

  /** Remove and return first item from queue (MobX action) */
  @action
  private shiftQueue(): ThumbnailQueueItem | undefined {
    return this.queue.shift();
  }

  /** Update statistics */
  @action
  private updateStats(): void {
    this.stats.remaining = this.queue.length;
    // Don't recalculate total - it's set when queuing items and should remain stable
  }

  /** Increment completed count (MobX action) */
  @action
  private incrementCompleted(): void {
    this.stats.completed++;
  }

  /** Increment failed count (MobX action) */
  @action
  private incrementFailed(): void {
    this.stats.failed++;
  }

  /** Set processing state (MobX action) */
  @action
  private setProcessingState(isProcessing: boolean, status: ThumbnailStatus): void {
    this.isProcessing = isProcessing;
    this.status = status;
  }

  /** Set current asset (MobX action) */
  @action
  private setCurrentAsset(asset: IAssetFile | null): void {
    this.currentAsset = asset;
  }

  /** Get current progress percentage */
  get progressPercentage(): number {
    if (this.stats.total === 0) return 0;
    return Math.round((this.stats.completed / this.stats.total) * 100);
  }

  /** Get status text for display */
  get statusText(): string {
    switch (this.status) {
      case ThumbnailStatus.Idle:
        return this.queue.length > 0 ? `${this.queue.length} thumbnails queued` : '';
      case ThumbnailStatus.Generating:
        return `Generating thumbnails... ${this.stats.completed}/${this.stats.total}`;
      case ThumbnailStatus.Completed:
        return this.stats.failed > 0
          ? `Thumbnails generation completed (${this.stats.failed} failed)`
          : '';
      case ThumbnailStatus.Error:
        return 'Thumbnail generation error';
      default:
        return '';
    }
  }

  /** Clean up thumbnails for specifically deleted assets */
  async cleanupDeletedThumbnails(deletedPaths: string[]): Promise<void> {
    if (deletedPaths.length === 0) return;

    try {
      const manifest = await this.getThumbnailManifest();
      const storageProvider = editorStore.project.makeScopedStorageProvider();

      let cleanupCount = 0;

      // Check only the deleted paths against the manifest
      for (const deletedPath of deletedPaths) {
        // Check for exact path match
        if (manifest[deletedPath]) {
          const entry = manifest[deletedPath];

          // Delete thumbnail file if it exists
          if (entry.thumbnailPath) {
            try {
              const thumbnailExists = await storageProvider.exists(
                ENGINE.AssetPath.fromString(entry.thumbnailPath),
                { silent: true }
              );
              if (thumbnailExists) {
                await storageProvider.deleteItem(ENGINE.AssetPath.fromString(entry.thumbnailPath));
                // Mark as deleted in cache
                this.thumbnailExistsCache.set(entry.thumbnailPath, false);
              }
            } catch (error) {
              console.warn(`[thumbnailService] Failed to delete thumbnail: ${entry.thumbnailPath}`, error);
            }
          }

          // Remove from manifest
          delete manifest[deletedPath];
          cleanupCount++;
        }

        // For deleted folders, also check for any assets that start with the folder path
        if (deletedPath.endsWith('/') || !deletedPath.includes('.')) {
          const folderPrefix = deletedPath.endsWith('/') ? deletedPath : deletedPath + '/';

          for (const [assetPath, entry] of Object.entries(manifest)) {
            if (assetPath.startsWith(folderPrefix)) {
              // Delete thumbnail file if it exists
              if (entry.thumbnailPath) {
                try {
                  const thumbnailExists = await storageProvider.exists(
                    ENGINE.AssetPath.fromString(entry.thumbnailPath),
                    { silent: true }
                  );
                  if (thumbnailExists) {
                    await storageProvider.deleteItem(ENGINE.AssetPath.fromString(entry.thumbnailPath));
                  }
                } catch (error) {
                  console.warn(`[thumbnailService] Failed to delete thumbnail: ${entry.thumbnailPath}`, error);
                }
              }

              // Remove from manifest
              delete manifest[assetPath];
              cleanupCount++;
            }
          }
        }
      }

      // Save updated manifest if any cleanup occurred
      if (cleanupCount > 0) {
        // Update cache with modified manifest
        this.manifestCache = manifest;

        const manifestPath = ENGINE.AssetPath.fromString('@project/.editor/thumbnails/.manifest.json');
        await storageProvider.uploadFile(
          manifestPath,
          JSON.stringify(manifest, null, 2),
          { contentType: 'application/json' }
        );

        if (DEBUG_LOG.cleanup) console.log(`[thumbnailService] Cleaned up ${cleanupCount} thumbnails for deleted assets.`);
      }
    } catch (error) {
      console.warn('[thumbnailService] Failed to cleanup deleted thumbnails:', error);
    }
  }

  /** Clean up stale thumbnails for assets that no longer exist or are outdated */
  async cleanupStaleThumbnails(currentAssets: IAssetFile[]): Promise<void> {
    try {
      const manifest = await this.getThumbnailManifest();
      const currentAssetPaths = new Set(currentAssets.map(asset => asset.path));
      const storageProvider = editorStore.project.makeScopedStorageProvider();

      let cleanupCount = 0;
      const now = Date.now();

      // Check each manifest entry
      for (const [assetPath, entry] of Object.entries(manifest)) {
        const shouldCleanup =
          // Asset no longer exists
          !currentAssetPaths.has(assetPath) &&
          // Entry is older than cleanup threshold
          (now - new Date(entry.generatedAt).getTime()) > this.CLEANUP_THRESHOLD;

        if (shouldCleanup) {
          // Delete thumbnail file if it exists
          if (entry.thumbnailPath) {
            try {
              const thumbnailExists = await storageProvider.exists(
                ENGINE.AssetPath.fromString(entry.thumbnailPath),
                { silent: true }
              );
              if (thumbnailExists) {
                await storageProvider.deleteItem(ENGINE.AssetPath.fromString(entry.thumbnailPath));
              }
            } catch (error) {
              console.warn(`[thumbnailService] Failed to delete orphaned thumbnail: ${entry.thumbnailPath}`, error);
            }
          }

          // Remove from manifest
          delete manifest[assetPath];
          cleanupCount++;
        }
      }

      // Save updated manifest if any cleanup occurred
      if (cleanupCount > 0) {
        // Update cache with modified manifest
        this.manifestCache = manifest;

        const manifestPath = ENGINE.AssetPath.fromString('@project/.editor/thumbnails/.manifest.json');
        await storageProvider.uploadFile(
          manifestPath,
          JSON.stringify(manifest, null, 2),
          { contentType: 'application/json' }
        );

        if (DEBUG_LOG.cleanup) console.log(`[thumbnailService] Cleaned up ${cleanupCount} stale thumbnails.`);
      }
    } catch (error) {
      console.warn('[thumbnailService] Failed to cleanup stale thumbnails:', error);
    }
  }

  /** Initialize thumbnail generation for current directory assets only */
  async initializeThumbnailsForDirectory(directoryPath: string, assets: IAssetFile[]): Promise<void> {
    const supportedAssets = assets.filter(asset => this.isSupported(asset.assetType) && !this.isExcludedAsset(asset));

    // Check which assets in the current directory need thumbnails
    const assetsNeedingThumbnails: IAssetFile[] = [];

    for (const asset of supportedAssets) {
      try {
        if (await this.needsGenerateThumbnail(asset)) {
          assetsNeedingThumbnails.push(asset);
        }
      } catch (error) {
        assetsNeedingThumbnails.push(asset); // Add to queue if check fails
      }
    }

    if (assetsNeedingThumbnails.length > 0 && DEBUG_LOG.directory) {
      console.debug(`[thumbnailService] Queuing ${assetsNeedingThumbnails.length} assets from directory "${directoryPath}" for thumbnail generation.`);
    }

    // Queue assets with high priority for visible directory
    assetsNeedingThumbnails.forEach(asset => {
      this.queueThumbnailGeneration(asset, 10); // High priority for current directory
    });
  }

  /** Handle directory change - prioritize thumbnails for newly visible assets (debounced) */
  onDirectoryChanged(directoryPath: string, visibleAssets: IAssetFile[]): void {
    // Clear existing debounce timeout
    if (this.directoryChangeTimeout) {
      clearTimeout(this.directoryChangeTimeout);
    }

    // Debounce directory changes to avoid rapid navigation causing excessive queue operations
    this.directoryChangeTimeout = setTimeout(() => {
      this.processDirectoryChange(directoryPath, visibleAssets);
    }, this.DIRECTORY_CHANGE_DEBOUNCE);
  }

  /** Process directory change after debounce */
  private async processDirectoryChange(directoryPath: string, visibleAssets: IAssetFile[]): Promise<void> {
    if (DEBUG_LOG.directoryChange) console.log(`[thumbnailService] Directory changed to: ${directoryPath}, processing ${visibleAssets.length} visible assets.`);

    // Cancel any low-priority background generation
    this.clearLowPriorityQueue();

    // Queue high-priority thumbnails for currently visible assets
    await this.initializeThumbnailsForDirectory(directoryPath, visibleAssets);
  }

  /** Clear low-priority items from queue (keep high-priority visible assets) */
  @action
  private clearLowPriorityQueue(): void {
    // Filter to keep only high-priority items (priority >= 5)
    const originalLength = this.queue.length;
    const highPriorityItems = this.queue.filter(item => item.priority >= 5);
    const removedCount = originalLength - highPriorityItems.length;

    // Update queue array
    this.queue.length = 0;
    this.queue.push(...highPriorityItems);

    // Update map - remove low-priority items
    for (const [path, item] of this.queueMap.entries()) {
      if (item.priority < 5) {
        this.queueMap.delete(path);
      }
    }

    // Adjust total for removed items
    this.stats.total = Math.max(0, this.stats.total - removedCount);

    this.updateStats();
  }

  /** Legacy method for backward compatibility - now processes current directory only */
  async initializeThumbnails(assets: IAssetFile[]): Promise<void> {
    // Get current directory from editor store
    const currentDirectory = editorStore.assets?.currentDirectory ?? '@root';

    // Filter assets to current directory only (non-recursive)
    const currentDirectoryAssets = assets.filter(asset => {
      if (currentDirectory === '@root') {
        // For root, show top-level items only
        const pathParts = asset.path.split('/');
        return pathParts.length <= 2; // e.g., "@project/file.ext" or "@engine/file.ext"
      }
      // For specific directories, show direct children only
      const assetDir = asset.path.substring(0, asset.path.lastIndexOf('/'));
      return assetDir === currentDirectory;
    });

    await this.initializeThumbnailsForDirectory(currentDirectory, currentDirectoryAssets);
  }
}

// ThumbnailService instances are created and managed by SceneWorldController
